/**
 * Validation du matériel et de la déclaration — partagée backend/frontend.
 *
 * Le frontend l'utilise pour afficher les erreurs champ par champ dans l'assistant ;
 * le backend la rejoue **intégralement** sur le corps de requête. Aucune règle ici ne
 * doit dépendre du DOM ni de Prisma : ce module reste pur et sans dépendance.
 *
 * Deux niveaux, volontairement distincts (le prototype les confondait) :
 *  - **bloquant** : seul le format du n° d'inventaire. La sauvegarde partielle est un
 *    choix produit — mieux vaut un matériel à moitié décrit qu'un matériel non déclaré.
 *  - **recommandé** : un champ vide est signalé, jamais refusé.
 */

import {
  CATEGORY_OF_TYPE,
  FIELDS,
  TYPE_BY_KEY,
  isSituationKey,
  isTypeAllowed,
  isTypeKey,
  type Etat,
  type FieldKey,
  type SituationKey,
  type TypeKey,
  type Usage,
} from './catalog.js';

// ---------------------------------------------------------------------------
// N° d'inventaire
// ---------------------------------------------------------------------------

/** Étiquette code-barres Groupe TDS : `EL-` suivi de 3 à 5 chiffres. */
export const NUM_INV_PATTERN = /^EL-[0-9]{3,5}$/;
export const NUM_INV_FORMAT_ERROR = 'Format : EL- suivi de 3 à 5 chiffres';
export const RECOMMENDED_FIELD_HINT = 'Champ recommandé';

/** Seuls ces types portent une étiquette d'inventaire dont le format est contrôlé. */
export const NUM_INV_TYPE_KEYS: readonly TypeKey[] = ['ORDI', 'TABLETTE', 'TEL', 'IMPRIMANTE'];

export function requiresNumInvFormat(typeKey: TypeKey): boolean {
  return NUM_INV_TYPE_KEYS.includes(typeKey);
}

/** Message d'unicité, construit ici pour rester identique côté API et côté UI. */
export function numInvConflictMessage(numInv: string, holder: string): string {
  return `Le n° d'inventaire ${numInv} est déjà utilisé par ${holder}.`;
}

// ---------------------------------------------------------------------------
// Matériel
// ---------------------------------------------------------------------------

export type EquipmentValues = Partial<Record<FieldKey, string>>;

export interface EquipmentInput {
  typeKey: TypeKey;
  vals: EquipmentValues;
  etat: Etat;
  usage: Usage;
}

const ETAT_KEYS: readonly string[] = ['OK', 'MOY', 'HS'];
const USAGE_KEYS: readonly string[] = ['SYS', 'OCC', 'NON'];

/** Ne conserve que les champs du type, débarrassés des espaces et des valeurs vides. */
export function cleanValues(typeKey: TypeKey, vals: EquipmentValues): EquipmentValues {
  const cleaned: EquipmentValues = {};
  for (const fieldKey of TYPE_BY_KEY[typeKey].fields) {
    const value = vals[fieldKey]?.trim();
    if (value) cleaned[fieldKey] = value;
  }
  return cleaned;
}

/**
 * Un matériel est « vide » quand il a des champs et qu'aucun n'est renseigné : l'utilisateur
 * a ajouté une ligne sans rien saisir, on l'ignore. Un type **sans aucun champ** (fauteuil,
 * glacière, panière…) n'est jamais vide : le déclarer suffit à l'enregistrer.
 */
export function isEquipmentEmpty(typeKey: TypeKey, vals: EquipmentValues): boolean {
  const { fields } = TYPE_BY_KEY[typeKey];
  if (fields.length === 0) return false;
  return fields.every((fieldKey) => !vals[fieldKey]?.trim());
}

/** Erreurs qui empêchent l'enregistrement. Aujourd'hui : le format du n° d'inventaire, et lui seul. */
export function blockingFieldErrors(
  typeKey: TypeKey,
  vals: EquipmentValues,
): Partial<Record<FieldKey, string>> {
  const errors: Partial<Record<FieldKey, string>> = {};
  const numInv = vals.numInv?.trim();
  if (numInv && requiresNumInvFormat(typeKey) && !NUM_INV_PATTERN.test(numInv)) {
    errors.numInv = NUM_INV_FORMAT_ERROR;
  }
  return errors;
}

/** Champs du type restés vides. Signalés à l'utilisateur, jamais bloquants. */
export function missingFields(typeKey: TypeKey, vals: EquipmentValues): FieldKey[] {
  return TYPE_BY_KEY[typeKey].fields.filter((fieldKey) => !vals[fieldKey]?.trim());
}

/** Résumé d'un matériel pour le récapitulatif et les listes (« Dell · TDS-PC-0421 »). */
export function equipmentSummary(typeKey: TypeKey, vals: EquipmentValues): string {
  const parts = TYPE_BY_KEY[typeKey].fields
    .filter((fieldKey) => FIELDS[fieldKey].kind !== 'area')
    .map((fieldKey) => {
      const value = vals[fieldKey]?.trim();
      if (!value) return null;
      return fieldKey === 'taille' ? `Taille ${value}` : value;
    })
    .filter((part): part is string => Boolean(part));
  return parts.join(' · ');
}

// ---------------------------------------------------------------------------
// Analyse des corps de requête — le serveur ne fait jamais confiance au client
// ---------------------------------------------------------------------------

export type ParseResult<T> = { ok: true; value: T } | { ok: false; error: string };

function asRecord(raw: unknown): Record<string, unknown> | null {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null;
}

/**
 * Valide un matériel soumis, dans le contexte d'une situation : le type doit exister *et*
 * être autorisé pour cette situation (on n'accepte pas un frigo déclaré en Home Office).
 * Les valeurs sont nettoyées ; les champs inconnus du type sont écartés silencieusement.
 */
export function parseEquipmentInput(raw: unknown, situation: SituationKey): ParseResult<EquipmentInput> {
  const body = asRecord(raw);
  if (!body) return { ok: false, error: 'Matériel invalide.' };

  const typeKey = body.typeKey;
  if (typeof typeKey !== 'string' || !isTypeKey(typeKey)) {
    return { ok: false, error: `Type de matériel inconnu : ${String(typeKey)}` };
  }
  if (!isTypeAllowed(typeKey, situation)) {
    return { ok: false, error: `Le type ${typeKey} n'est pas déclarable dans cette situation.` };
  }

  const rawVals = asRecord(body.vals) ?? {};
  const vals: EquipmentValues = {};
  for (const [key, value] of Object.entries(rawVals)) {
    if (typeof value === 'string') vals[key as FieldKey] = value;
  }
  const cleaned = cleanValues(typeKey, vals);

  const errors = blockingFieldErrors(typeKey, cleaned);
  const firstError = Object.values(errors)[0];
  if (firstError) return { ok: false, error: firstError };

  const etat = typeof body.etat === 'string' && ETAT_KEYS.includes(body.etat) ? body.etat : 'OK';
  const usage = typeof body.usage === 'string' && USAGE_KEYS.includes(body.usage) ? body.usage : 'SYS';

  return { ok: true, value: { typeKey, vals: cleaned, etat: etat as Etat, usage: usage as Usage } };
}

export interface DeclarationInput {
  /** « Je n'ai aucun matériel TDS » — aucune situation, aucun équipement. */
  noEquipment: boolean;
  situation: SituationKey | null;
  /** Id Bécane du site, obligatoire si `situation === 'FIXE'`. */
  becaneSiteId: number | null;
  equipments: EquipmentInput[];
}

export function parseDeclarationInput(raw: unknown): ParseResult<DeclarationInput> {
  const body = asRecord(raw);
  if (!body) return { ok: false, error: 'Déclaration invalide.' };

  if (body.noEquipment === true) {
    return { ok: true, value: { noEquipment: true, situation: null, becaneSiteId: null, equipments: [] } };
  }

  const situation = body.situation;
  if (typeof situation !== 'string' || !isSituationKey(situation)) {
    return { ok: false, error: 'Situation manquante ou inconnue.' };
  }

  let becaneSiteId: number | null = null;
  if (situation === 'FIXE') {
    const id = Number(body.becaneSiteId);
    if (!Number.isInteger(id) || id <= 0) {
      return { ok: false, error: 'Un site doit être choisi pour une situation « Site fixe ».' };
    }
    becaneSiteId = id;
  }

  const rawEquipments = Array.isArray(body.equipments) ? body.equipments : [];
  const equipments: EquipmentInput[] = [];
  for (const rawEquipment of rawEquipments) {
    const parsed = parseEquipmentInput(rawEquipment, situation);
    if (!parsed.ok) return parsed;
    // Une ligne ajoutée puis laissée vide n'est pas une erreur : elle est simplement ignorée.
    if (isEquipmentEmpty(parsed.value.typeKey, parsed.value.vals)) continue;
    equipments.push(parsed.value);
  }

  return { ok: true, value: { noEquipment: false, situation, becaneSiteId, equipments } };
}

/** Catégorie du matériel, dérivée du type. Stockée dénormalisée pour les filtres de l'inventaire. */
export function categoryOf(typeKey: TypeKey) {
  return CATEGORY_OF_TYPE[typeKey];
}
